"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {type KeenSliderInstance, useKeenSlider} from "keen-slider/react";

import {Typography} from "@/shared/ui/data-display/typography";

import "./styles/hero-slider.scss";
import "keen-slider/keen-slider.min.css";

export interface HeroSliderImage {
    src: string;
    alt: string;
    width?: number;
    height?: number;
}

interface HeroSliderProps {
    images: Array<HeroSliderImage>;
    label?: string;
}

export function HeroSlider({images, label = "As seen in"}: HeroSliderProps): JSX.Element | null {
    const [_sliderInstance, setSliderInstance] = useState<KeenSliderInstance | null>(null);

    const [sliderRef] = useKeenSlider(
        {
            slides: {
                perView: 5,
                spacing: 10,
            },
            drag: true,
            loop: images.length > 1,
            mode: "snap",
            breakpoints: {
                "(max-width: 767px)": {
                    slides: {
                        perView: 3,
                        spacing: 0,
                    },
                    drag: false,
                    loop: false,
                },
            },
        },
        [
            (slider) => {
                setSliderInstance(slider);
            },
        ],
    );

    if (!images || images.length === 0) {
        return null;
    }

    return (
        <div className="hero__slider">
            <div className="hero__slider-before">
                <Typography as="p" size={10} weight={500} color="white">
                    {label}
                </Typography>
            </div>
            <div ref={sliderRef} className="keen-slider hero__slider-track">
                {images.map((image, index) => {
                    const hasDimensions = image.width && image.height;

                    return (
                        <div key={index} className="keen-slider__slide hero__slider-slide">
                            <div className="hero__slider-image">
                                {hasDimensions ? (
                                    <SmartImage
                                        src={image.src}
                                        alt={image.alt}
                                        width={image.width}
                                        height={image.height}
                                        priority={index === 0}
                                        style={{objectFit: "contain"}}
                                    />
                                ) : (
                                    <SmartImage
                                        fill
                                        src={image.src}
                                        alt={image.alt}
                                        priority={index === 0}
                                        sizes="100vw"
                                        style={{objectFit: "contain"}}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
